import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Diamond, Eye, EyeSlash, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("admin@jewellery.com");
  const [password, setPassword] = useState("admin123");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (user && user !== false) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      setErr("Please enter your email/mobile and password");
      return;
    }
    setLoading(true);
    setErr("");
    const res = await login(identifier.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      setErr(res.error);
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden grid md:grid-cols-2">
      {/* Left visual */}
      <div className="hidden md:flex relative items-end p-12 overflow-hidden">
        <img
          src="https://images.pexels.com/photos/15622999/pexels-photo-15622999.jpeg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/60 to-transparent" />
        <div className="auth-orb h-72 w-72 bg-primary -top-10 -left-10" />
        <div className="relative z-10 max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/15 border border-primary/40 grid place-items-center backdrop-blur-md">
              <Diamond size={26} weight="fill" className="text-gold" />
            </div>
            <div>
              <div className="font-display text-3xl tracking-tight">AURUM</div>
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                Jewellery ERP
              </div>
            </div>
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
            The retail engine for{" "}
            <span className="text-gold">modern jewellers</span>.
          </h1>
          <p className="text-muted-foreground max-w-sm leading-relaxed">
            POS, inventory, customer ledgers, old gold exchange, and reports —
            built for Indian jewellery stores. One workspace. All branches.
          </p>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground pt-4">
            <Sparkle size={14} weight="fill" className="text-gold" />
            v1.0 · Single-tenant ready
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="relative flex items-center justify-center p-6 md:p-12">
        <div className="auth-orb h-80 w-80 bg-primary -bottom-20 -right-20" />
        <div className="w-full max-w-md relative z-10">
          <div className="md:hidden flex items-center gap-2 mb-8">
            <Diamond size={22} weight="fill" className="text-gold" />
            <span className="font-display text-xl">AURUM</span>
          </div>

          <div className="mb-8">
            <h2 className="font-display text-3xl tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Enter your credentials to access the workspace.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="space-y-5"
            data-testid="login-form"
          >
            <div>
              <Label
                htmlFor="identifier"
                className="text-[11px] tracking-[0.15em] uppercase text-muted-foreground"
              >
                Email or Mobile
              </Label>
              <Input
                id="identifier"
                data-testid="login-identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@store.com or 98765 43210"
                className="mt-1.5 h-11 bg-card border-border focus-visible:ring-primary"
                autoComplete="username"
              />
            </div>
            <div>
              <Label
                htmlFor="password"
                className="text-[11px] tracking-[0.15em] uppercase text-muted-foreground"
              >
                Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pr-10 bg-card border-border focus-visible:ring-primary"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Toggle password visibility"
                >
                  {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              loading={loading}
              className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] transition-all"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>

            <div className="text-xs text-muted-foreground text-center pt-2">
              Default admin:{" "}
              <span className="text-gold">admin@jewellery.com</span> /{" "}
              <span className="text-gold">admin123</span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
